import { z } from 'zod';
import {
    CardPointeRpaCapabilityName,
    CardPointeRpaCapabilityStatus,
    CardPointeRpaWorkflowId,
    CardPointeRpaWorkflowLoadingOutcome,
    type CardPointeRpaCapability,
    type CardPointeRpaCapabilityMetadata,
    type CardPointeRpaWorkflowDescriptor,
    type CardPointeRpaWorkflowLoadingNormalizedResult,
    type CardPointeRpaWorkflowLoadingRequestDetails,
    type CardPointeRpaWorkflowLoadingResponseDetails,
} from '../../../types/ipg/cardpointe/cardpointe-rpa-workflow-loading.types';

export const CardPointeRpaCapabilityNameSchema: z.ZodType<
    (typeof CardPointeRpaCapabilityName)[keyof typeof CardPointeRpaCapabilityName]
> = z.enum([
    CardPointeRpaCapabilityName.CARD_PRESENT_TERMINAL_SALE,
    CardPointeRpaCapabilityName.CARD_NOT_PRESENT_TOKEN_SALE,
    CardPointeRpaCapabilityName.STORED_CARD_PAYMENT,
    CardPointeRpaCapabilityName.TRANSACTION_STATUS_LOOKUP,
    CardPointeRpaCapabilityName.TERMINAL_ABORT,
    CardPointeRpaCapabilityName.TERMINAL_TOKENIZATION,
    CardPointeRpaCapabilityName.ACH_PAYMENT,
]);

export const CardPointeRpaWorkflowIdSchema: z.ZodType<
    (typeof CardPointeRpaWorkflowId)[keyof typeof CardPointeRpaWorkflowId]
> = z.enum([
    CardPointeRpaWorkflowId.TERMINAL_SALE,
    CardPointeRpaWorkflowId.TERMINAL_ABORT,
    CardPointeRpaWorkflowId.RECORD_CARD_TOKEN,
    CardPointeRpaWorkflowId.CNP_TOKEN_SALE,
    CardPointeRpaWorkflowId.STORED_CARD_PAYMENT,
    CardPointeRpaWorkflowId.TRANSACTION_STATUS,
    CardPointeRpaWorkflowId.ACH_PAYMENT,
]);

export const CardPointeEnabledRpaCapabilitySchema: z.ZodObject<{
    status: z.ZodLiteral<typeof CardPointeRpaCapabilityStatus.ENABLED>;
}> = z
    .object({
        status: z.literal(CardPointeRpaCapabilityStatus.ENABLED),
    })
    .strict();

export const CardPointeDisabledRpaCapabilitySchema: z.ZodObject<{
    status: z.ZodLiteral<typeof CardPointeRpaCapabilityStatus.DISABLED>;
    disabledReason: z.ZodString;
}> = z
    .object({
        status: z.literal(CardPointeRpaCapabilityStatus.DISABLED),
        disabledReason: z.string().min(1),
    })
    .strict();

export const CardPointeUnsupportedRpaCapabilitySchema: z.ZodObject<{
    status: z.ZodLiteral<typeof CardPointeRpaCapabilityStatus.UNSUPPORTED>;
    unsupportedReason: z.ZodString;
}> = z
    .object({
        status: z.literal(CardPointeRpaCapabilityStatus.UNSUPPORTED),
        unsupportedReason: z.string().min(1),
    })
    .strict();

export const CardPointeRpaCapabilitySchema: z.ZodType<CardPointeRpaCapability> =
    z.discriminatedUnion('status', [
        CardPointeEnabledRpaCapabilitySchema,
        CardPointeDisabledRpaCapabilitySchema,
        CardPointeUnsupportedRpaCapabilitySchema,
    ]);

export const CardPointeRpaCapabilityMetadataSchema: z.ZodObject<{
    card_present_terminal_sale: typeof CardPointeRpaCapabilitySchema;
    card_not_present_token_sale: typeof CardPointeRpaCapabilitySchema;
    stored_card_payment: typeof CardPointeRpaCapabilitySchema;
    transaction_status_lookup: typeof CardPointeRpaCapabilitySchema;
    terminal_abort: typeof CardPointeRpaCapabilitySchema;
    terminal_tokenization: typeof CardPointeRpaCapabilitySchema;
    ach_payment: typeof CardPointeRpaCapabilitySchema;
}> = z
    .object({
        [CardPointeRpaCapabilityName.CARD_PRESENT_TERMINAL_SALE]:
            CardPointeRpaCapabilitySchema,
        [CardPointeRpaCapabilityName.CARD_NOT_PRESENT_TOKEN_SALE]:
            CardPointeRpaCapabilitySchema,
        [CardPointeRpaCapabilityName.STORED_CARD_PAYMENT]:
            CardPointeRpaCapabilitySchema,
        [CardPointeRpaCapabilityName.TRANSACTION_STATUS_LOOKUP]:
            CardPointeRpaCapabilitySchema,
        [CardPointeRpaCapabilityName.TERMINAL_ABORT]:
            CardPointeRpaCapabilitySchema,
        [CardPointeRpaCapabilityName.TERMINAL_TOKENIZATION]:
            CardPointeRpaCapabilitySchema,
        [CardPointeRpaCapabilityName.ACH_PAYMENT]: CardPointeRpaCapabilitySchema,
    })
    .strict() satisfies z.ZodType<CardPointeRpaCapabilityMetadata>;

export const CardPointeRpaWorkflowLoadingRequestDetailsSchema: z.ZodObject<{
    packageName: z.ZodLiteral<'cardpointe-ipg'>;
    requestedWorkflowIds: z.ZodArray<typeof CardPointeRpaWorkflowIdSchema>;
}> = z
    .object({
        packageName: z.literal('cardpointe-ipg'),
        requestedWorkflowIds: z.array(CardPointeRpaWorkflowIdSchema).min(1),
    })
    .strict() satisfies z.ZodType<CardPointeRpaWorkflowLoadingRequestDetails>;

export const CardPointeRpaWorkflowDescriptorSchema: z.ZodObject<{
    id: typeof CardPointeRpaWorkflowIdSchema;
    capabilityName: typeof CardPointeRpaCapabilityNameSchema;
    enabled: z.ZodBoolean;
    showInMenu: z.ZodBoolean;
}> = z
    .object({
        id: CardPointeRpaWorkflowIdSchema,
        capabilityName: CardPointeRpaCapabilityNameSchema,
        enabled: z.boolean(),
        showInMenu: z.boolean(),
    })
    .strict() satisfies z.ZodType<CardPointeRpaWorkflowDescriptor>;

export const CardPointeRpaWorkflowLoadingNormalizedResultSchema: z.ZodObject<{
    packageName: z.ZodLiteral<'cardpointe-ipg'>;
    workflowIds: z.ZodArray<typeof CardPointeRpaWorkflowIdSchema>;
    loadedWorkflowCount: z.ZodNumber;
    enabledWorkflowCount: z.ZodNumber;
    capabilities: typeof CardPointeRpaCapabilityMetadataSchema;
}> = z
    .object({
        packageName: z.literal('cardpointe-ipg'),
        workflowIds: z.array(CardPointeRpaWorkflowIdSchema),
        loadedWorkflowCount: z.number().int().nonnegative(),
        enabledWorkflowCount: z.number().int().nonnegative(),
        capabilities: CardPointeRpaCapabilityMetadataSchema,
    })
    .strict() satisfies z.ZodType<CardPointeRpaWorkflowLoadingNormalizedResult>;

export const CardPointeRpaWorkflowLoadingLoadedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeRpaWorkflowLoadingOutcome.LOADED>;
    workflows: z.ZodArray<typeof CardPointeRpaWorkflowDescriptorSchema>;
    normalizedResult: typeof CardPointeRpaWorkflowLoadingNormalizedResultSchema;
}> = z
    .object({
        outcome: z.literal(CardPointeRpaWorkflowLoadingOutcome.LOADED),
        workflows: z.array(CardPointeRpaWorkflowDescriptorSchema).min(1),
        normalizedResult: CardPointeRpaWorkflowLoadingNormalizedResultSchema,
    })
    .strict();

export const CardPointeRpaWorkflowLoadingMissingPackageResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<
        typeof CardPointeRpaWorkflowLoadingOutcome.MISSING_PACKAGE
    >;
    packageName: z.ZodLiteral<'cardpointe-ipg'>;
    message: z.ZodString;
}> = z
    .object({
        outcome: z.literal(CardPointeRpaWorkflowLoadingOutcome.MISSING_PACKAGE),
        packageName: z.literal('cardpointe-ipg'),
        message: z.string().min(1),
    })
    .strict();

export const CardPointeRpaWorkflowLoadingMalformedWorkflowsResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<
        typeof CardPointeRpaWorkflowLoadingOutcome.MALFORMED_WORKFLOWS
    >;
    packageName: z.ZodLiteral<'cardpointe-ipg'>;
    message: z.ZodString;
    workflowIds: z.ZodOptional<z.ZodArray<z.ZodString>>;
}> = z
    .object({
        outcome: z.literal(
            CardPointeRpaWorkflowLoadingOutcome.MALFORMED_WORKFLOWS
        ),
        packageName: z.literal('cardpointe-ipg'),
        message: z.string().min(1),
        workflowIds: z.array(z.string().min(1)).optional(),
    })
    .strict();

export const CardPointeRpaWorkflowLoadingResponseDetailsSchema: z.ZodType<CardPointeRpaWorkflowLoadingResponseDetails> =
    z.discriminatedUnion('outcome', [
        CardPointeRpaWorkflowLoadingLoadedResponseDetailsSchema,
        CardPointeRpaWorkflowLoadingMissingPackageResponseDetailsSchema,
        CardPointeRpaWorkflowLoadingMalformedWorkflowsResponseDetailsSchema,
    ]);
